import { type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from "react";
import type { UiEvent } from "../../server/src/shared-types";
import { api, ApiError, fetchConfig, type Me, streamEvents } from "./api";
import { initAuth } from "./auth";
import { ConfirmationCard, IncidentLink, RichText, TypingIndicator } from "./components";
import { detectLanguage, I18nContext, type Language, MESSAGES } from "./i18n";
import { initTeams } from "./teams";
import { buildTimeline, mergeEvents } from "./timeline";

const storageKey = (oid: string) => `movik-incidents:session:${oid}`;

function readStored(key: string): string | null {
  try {
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function writeStored(key: string, value: string | null): void {
  try {
    if (value) localStorage.setItem(key, value);
    else localStorage.removeItem(key);
  } catch {
    // Storage unavailable (e.g. private mode): the conversation just isn't resumed.
  }
}

type Boot = { status: "loading" } | { status: "ready"; me: Me } | { status: "error" };

export function App() {
  // Browser language until Teams reports the user's locale.
  const [language, setLanguage] = useState<Language>(() => detectLanguage(undefined));
  const t = MESSAGES[language];
  const [boot, setBoot] = useState<Boot>({ status: "loading" });
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [events, setEvents] = useState<UiEvent[]>([]);
  const [pending, setPending] = useState<string[]>([]);
  const [draft, setDraft] = useState("");
  const [busyConfirmation, setBusyConfirmation] = useState<string | null>(null);
  // Incremented on every message or confirmation sent, to reopen the stream.
  const [wakeups, setWakeups] = useState(0);
  const [actionError, setActionError] = useState<"send" | "confirm" | null>(null);
  const bottomRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLTextAreaElement>(null);

  const receive = useCallback((incoming: UiEvent[]) => {
    setEvents((current) => mergeEvents(current, incoming));
    const echoed = incoming.filter((e) => e.kind === "message" && e.role === "user");
    if (echoed.length) {
      setPending((current) => {
        const next = [...current];
        for (const e of echoed) {
          const idx = next.indexOf((e as { text: string }).text);
          if (idx >= 0) next.splice(idx, 1);
        }
        return next;
      });
    }
  }, []);

  // Startup: Teams, SSO and resuming the last conversation.
  useEffect(() => {
    (async () => {
      try {
        setLanguage(detectLanguage(await initTeams()));
        await initAuth(await fetchConfig());
        const me = await api.me();
        const stored = readStored(storageKey(me.oid));
        if (stored) {
          try {
            const snapshot = await api.snapshot(stored);
            setEvents(snapshot.events);
            setSessionId(stored);
          } catch (err) {
            if (err instanceof ApiError && err.status === 404) writeStored(storageKey(me.oid), null);
            else throw err;
          }
        }
        setBoot({ status: "ready", me });
      } catch (err) {
        console.error(err);
        setBoot({ status: "error" });
      }
    })();
  }, []);

  // Follows the conversation live while the agent works. The backend closes the stream when the
  // session settles (end of turn or card waiting for the person); if it drops earlier, reconnect.
  useEffect(() => {
    if (!sessionId) return;
    const controller = new AbortController();
    (async () => {
      while (!controller.signal.aborted) {
        try {
          const outcome = await streamEvents(
            sessionId,
            (event) => receive([event]),
            () => api.snapshot(sessionId).then((s) => receive(s.events)).catch(() => undefined),
            controller.signal,
          );
          if (outcome === "settled") return;
        } catch (err) {
          if (controller.signal.aborted) return;
          if (err instanceof ApiError && err.status === 404) return;
          console.warn("Stream dropped, reconnecting…", err);
        }
        await new Promise((r) => setTimeout(r, 2000));
      }
    })();
    return () => controller.abort();
  }, [sessionId, wakeups, receive]);

  useEffect(() => {
    document.documentElement.lang = { pt: "pt-BR", en: "en", es: "es" }[language];
  }, [language]);

  const timeline = useMemo(() => buildTimeline(events, pending), [events, pending]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [timeline.items.length, timeline.state]);

  const me = boot.status === "ready" ? boot.me : null;
  const { ended } = timeline;
  const canType = !!me && !timeline.pendingConfirmationId && !ended;

  async function send(text: string) {
    if (!me || !text.trim()) return;
    const message = text.trim();
    setDraft("");
    setActionError(null);
    setPending((p) => [...p, message]);
    try {
      let sid = sessionId;
      if (!sid) {
        sid = await api.createSession();
        writeStored(storageKey(me.oid), sid);
        setSessionId(sid);
      }
      await api.sendMessage(sid, message);
      setWakeups((n) => n + 1);
    } catch (err) {
      console.error(err);
      setPending((p) => p.filter((m) => m !== message));
      setDraft(message);
      setActionError("send");
    }
  }

  async function answer(toolUseId: string, approved: boolean, note?: string) {
    if (!sessionId) return;
    setBusyConfirmation(toolUseId);
    setActionError(null);
    try {
      await api.confirm(sessionId, toolUseId, approved, note);
      setWakeups((n) => n + 1);
    } catch (err) {
      console.error(err);
      setActionError("confirm");
    } finally {
      setBusyConfirmation(null);
    }
  }

  function startOver() {
    if (!me) return;
    writeStored(storageKey(me.oid), null);
    setSessionId(null);
    setEvents([]);
    setPending([]);
    setActionError(null);
    setDraft("");
    inputRef.current?.focus();
  }

  const localized = (node: ReactNode) => <I18nContext.Provider value={t}>{node}</I18nContext.Provider>;

  if (boot.status === "loading") {
    return localized(
      <main className="center">
        <TypingIndicator />
      </main>,
    );
  }
  if (boot.status === "error") {
    return localized(
      <main className="center">
        <p className="boot-error">{t.errors.boot}</p>
      </main>,
    );
  }

  const firstName = boot.me.name.split(" ")[0] || boot.me.name;
  const empty = timeline.items.length === 0;

  return localized(
    <div className="app">
      <header className="topbar">
        <div>
          <h1>{t.title}</h1>
          <p>{t.subtitle}</p>
        </div>
        {!empty && (
          <button type="button" className="btn btn-ghost" onClick={startOver}>
            {t.newReport}
          </button>
        )}
      </header>

      <main className="conversation" aria-live="polite">
        {empty ? (
          <section className="welcome">
            <h2>{t.greeting(firstName)}</h2>
            <p>{t.intro}</p>
            <p className="languages">{t.languages}</p>
            <div className="examples">
              {t.examples.map((example) => (
                <button key={example} type="button" className="chip" onClick={() => send(example)}>
                  {example}
                </button>
              ))}
            </div>
          </section>
        ) : (
          timeline.items.map((item) => {
            switch (item.type) {
              case "message":
                return (
                  <div key={item.id} className={`bubble bubble-${item.role} ${item.pending ? "bubble-pending" : ""}`}>
                    <RichText text={item.text} />
                  </div>
                );
              case "activity":
                return (
                  <p key={item.id} className="activity">
                    {t.activity(item.activity, item.issue)}
                  </p>
                );
              case "confirmation":
                return (
                  <ConfirmationCard
                    key={item.id}
                    item={item}
                    active={timeline.pendingConfirmationId === item.id}
                    busy={busyConfirmation === item.id}
                    onApprove={() => answer(item.id, true)}
                    onReject={(note) => answer(item.id, false, note)}
                  />
                );
              case "incident":
                return <IncidentLink key={item.id} item={item} />;
            }
          })
        )}
        {timeline.state === "working" && <TypingIndicator />}
        {timeline.state === "error" && timeline.error && (
          <div className="banner banner-error">
            {t.status[timeline.error]}
            {ended && (
              <button type="button" className="btn btn-secondary" onClick={startOver}>
                {t.newReport}
              </button>
            )}
          </div>
        )}
        {actionError && <div className="banner banner-error">{t.errors[actionError]}</div>}
        <div ref={bottomRef} />
      </main>

      <form
        className="composer"
        onSubmit={(e) => {
          e.preventDefault();
          void send(draft);
        }}
      >
        <textarea
          ref={inputRef}
          value={draft}
          onChange={(e) => setDraft(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey && !e.nativeEvent.isComposing) {
              e.preventDefault();
              void send(draft);
            }
          }}
          rows={1}
          maxLength={8000}
          disabled={!canType}
          placeholder={
            timeline.pendingConfirmationId
              ? t.placeholder.pendingCard
              : ended
                ? t.placeholder.ended
                : t.placeholder.default
          }
          aria-label={t.messageLabel}
        />
        <button type="submit" className="btn btn-primary" disabled={!canType || !draft.trim()}>
          {t.send}
        </button>
      </form>
    </div>,
  );
}
